import {
  Client,
  client,
  DataCollector,
  dataCollector,
  ThreeDSecure,
  threeDSecure,
  ThreeDSecureVerifyPayload,
} from 'braintree-web';
import { TFunction } from 'react-i18next';

import { ThreeDSecureVerifyPayloadOverride } from '../components/CreditCard/Braintree/BraintreeAuthorizer';
import { Additional3dsInfo, CardType } from '../types';
import {
  convertAddress,
  chunks,
  digitsOnly,
  formatAdditionalInformation,
} from '../utils/braintreeUtils';

import { braintreeTracking } from './diiService';
import { captureException } from './sentryService';

// Some of the possible values: https://developer.paypal.com/braintree/docs/reference/response/credit-card#card_type
// Note that it is not comprehensive (ex: UK Maestro is missing)
const NICE_TYPES_TO_TYPE: { [key: string]: CardType } = {
  visa: 'visa',
  mastercard: 'mastercard',
  americanexpress: 'american-express',
  dinersclub: 'diners-club',
  discover: 'discover',
  jcb: 'jcb',
  unionpay: 'unionpay',
  ukmaestro: 'maestro',
  maestro: 'maestro',
  elo: 'elo',
  mir: 'mir',
  hiper: 'hiper',
  hipercard: 'hipercard',
};

export const sanitizeCardType = (inputType: string) => {
  const mapping =
    NICE_TYPES_TO_TYPE[
      inputType.replace(' ', '').replace('-', '').toLowerCase() as keyof typeof NICE_TYPES_TO_TYPE
    ];
  if (!mapping) {
    throw new Error('Cannot convert credit card niceType : ' + inputType);
  }
  return mapping;
};

export const sanitizeCardTypes = (inputTypes: string[]) => {
  return [...new Set(inputTypes.map((type) => sanitizeCardType(type)))];
};

type AuthorizePaymentResultError = {
  success: false;
  errorMessage: string;
  retryable: boolean;
};

type AuthorizePaymentResultSuccess = {
  success: true;
  nonce: string;
  liabilityShifted: boolean;
  liabilityShiftPossible: boolean;
  status:
    | 'authenticate_successful'
    | 'authenticate_attempt_successful'
    | 'authenticate_successful_issuer_not_participating'
    | 'authentication_bypassed';
  deviceData: string;
};

export type AuthorizePaymentResult = AuthorizePaymentResultError | AuthorizePaymentResultSuccess;

export const authorizePayment = async ({
  t,
  token,
  bin,
  nonce,
  amount,
  additional3dsInfo,
}: {
  t: TFunction;
  token: string;
  bin: string;
  nonce: string;
  amount: number;
  additional3dsInfo?: Additional3dsInfo;
}): Promise<AuthorizePaymentResult> => {
  const buildInitializationError = (): AuthorizePaymentResultError => {
    return {
      success: false,
      errorMessage: t('checkout.payment.creditcard.error.method.unavailable'),
      retryable: false,
    };
  };
  const buildRetryError = (): AuthorizePaymentResultError => {
    return {
      success: false,
      errorMessage: t('checkout.payment.creditcard.error.authorize.retry'),
      retryable: true,
    };
  };
  const buildNonRetryError = (): AuthorizePaymentResultError => {
    return {
      success: false,
      errorMessage: t('checkout.payment.creditcard.error.authorize.non.retry'),
      retryable: false,
    };
  };

  let braintreeClient: Client;
  try {
    braintreeClient = await client.create({ authorization: token });
  } catch (error: any) {
    captureException({
      error,
      message: 'Cannot create Braintree client',
    });
    return buildInitializationError();
  }

  let braintreeDataCollector: DataCollector;
  try {
    braintreeDataCollector = await dataCollector.create({
      client: braintreeClient,
    });
  } catch (error: any) {
    captureException({
      error,
      message: 'Data collector cannot be created',
    });
    return buildInitializationError();
  }

  let threeDSecureInstance: ThreeDSecure;
  let modalHasRendered = false;
  try {
    threeDSecureInstance = await threeDSecure.create({
      authorization: token,
      version: 2,
    });
    threeDSecureInstance.on('authentication-modal-render', (_, next) => {
      braintreeTracking('3DSdisplay');
      modalHasRendered = true;
      next?.();
    });
    threeDSecureInstance.on('customer-canceled', (_, next) => {
      braintreeTracking('3DScancel');
      next?.();
    });
  } catch (error: any) {
    captureException({
      error,
      message: '3DSecure instance cannot be created',
    });
    return buildInitializationError();
  }

  let basePayload: ThreeDSecureVerifyPayload;
  try {
    // Documentation: https://braintree.github.io/braintree-web/current/ThreeDSecure.html#verifyCard
    basePayload = await threeDSecureInstance.verifyCard({
      nonce,
      bin,
      amount,
      email: chunks(additional3dsInfo?.email, 255),
      mobilePhoneNumber: chunks(digitsOnly(additional3dsInfo?.shippingAddress?.phoneNumber), 25),
      billingAddress: convertAddress('billing', additional3dsInfo?.billingAddress),
      additionalInformation: formatAdditionalInformation(additional3dsInfo),
      onLookupComplete: (data: any, next: any) => {
        next();
      },
    } as any);

    if (!modalHasRendered) {
      braintreeTracking('3DSfrictionless');
    }
  } catch (error: any) {
    if (!modalHasRendered) {
      braintreeTracking('3DSfrictionless');
    }
    braintreeTracking('3DSsubmitKObackend', error);
    // Error on Authentication
    // >>> An error occurred while attempting to authenticate. Alternatively, merchants can ask customers for an alternative form of payment.
    // Test card: 4000000000001125 (VISA)
    // Action : Show message Technical error, please try again - if error persists, try a different payment method.
    captureException({
      error,
      message: 'An internal error happened during 3DS',
    });
    return buildRetryError();
  }

  const paymentMethodPayload = basePayload as any as ThreeDSecureVerifyPayloadOverride;
  const { threeDSecureInfo } = paymentMethodPayload;

  // https://wiki.web.zooplus.de/display/PD/Error+Handling+during+Braintree+payment+process
  switch (threeDSecureInfo.status) {
    case 'authenticate_successful':
      // Successful No-Challenge Authentication
      // >>> Cardholder enrolled, authentication successful, and signature verification successful.
      // Test card: 4000000000001000 (VISA)

      // Successful Challenge Authentication
      // >>> Cardholder enrolled, authentication successful, and signature verification successful.
      // Test card: 4000000000001091 (VISA)

      // Action : success, proceed with order creation
      braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      break;
    case 'authenticate_attempt_successful':
      //Attempt No-Challenge Authentication
      /**
       * The provided card brand authenticated this 3D Secure transaction without password confirmation
       * from the customer.
       *  */

      // Test card: 4000000000001026    MM/YYY: 01/20**
      // Action : success, proceed with order creation
      // Liability Shifted: True
      braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      break;
    case 'authenticate_successful_issuer_not_participating': // deprecated on BT side but we are handling it just in case
      /**
       * Your customer’s card-issuing bank is domiciled outside of the US and is not certified by the associated card brand to allow
       * 3D Secure transactions.
       */
      // Test card: No testcard is provide by Braintree
      // Action : success, proceed with order creation
      // Liability Shifted: True
      braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      break;
    case 'authentication_bypassed':
      /**
       * The customer's card was issued by a bank where 3D Secure authentication steps are bypassed. If you prefer not to continue
       * transactions with this status, ask the customer for a different payment method.
       */
      // Test card: No testcard is provide by Braintree
      // Action : success, proceed with order creation
      // Liability Shifted: True
      braintreeTracking('3DSsubmitOK', threeDSecureInfo.status);
      break;
    case 'authenticate_frictionless_failed':
      // Failed No-Challenge Authentication
      // >>> Cardholder enrolled, authentication unsuccessful. Merchants should prompt customers for another form of payment.
      // Test card: 4000000000001018 (VISA)

      // Action : 3D secure check failed, show error message & ask for different card/payment method
      captureException({
        message: 'authenticate_frictionless_failed',
      });
      braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      return buildRetryError();
    case 'authentication_unavailable':
      // Attempt No-Challenge Authentication
      // >>> The provided card brand authenticated this 3D Secure transaction without password confirmation from the customer.
      // Test card: 4000000000001026 (VISA)

      // Authentication Not Available on Lookup
      // >>> Authentication unavailable for this transaction.
      // Test card: 4000000000001059 (VISA)

      // Error on Lookup
      // >>> An error occurred while attempting to lookup enrollment.
      // Test card: 4000000000001067 (VISA)

      // Timeout on Lookup
      // >>> Attempting to lookup enrollment resulted in a timeout.
      // Test card: 4000000000001075 (VISA)

      // Action : Show message: We could not verify your card, please try again or use a different card / payment method
      captureException({
        message: 'authentication_unavailable',
      });
      braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      return buildRetryError();
    case 'authenticate_unable_to_authenticate':
      // Unavailable No-Challenge Authentication from the Issuer
      // >>> Authentication unavailable for this transaction.
      // Test card: 4000000000001034 (VISA)

      // Data Only Successful
      // >>>The data-only 3D Secure call was successfully created. The dataOnlyRequested flag must be sent to receive a successful response.
      // @See https://developer.paypal.com/braintree/docs/guides/3d-secure/advanced-options#using-data-only-3d-secure
      // Test card: 5119737947565580 (MASTERCARD)

      // Action : Show message: We could not verify your card, please try again or use a different card / payment method
      captureException({
        message: 'authenticate_unable_to_authenticate',
      });
      braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      return buildRetryError();
    case 'authenticate_rejected':
      // Rejected No-Challenge Authentication by the Issuer
      // >>> Authentication unsuccessful. Merchants should prompt customers for another form of payment.
      // Test card: 4000000000001042 (VISA)

      // Action : Show message: Verification failed, please use a different card / payment method
      captureException({
        message: 'authenticate_rejected',
      });
      braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      return buildNonRetryError();
    case 'lookup_bypassed':
      // Bypassed Authentication
      // >>> Bypass used to simulate a scenario where merchant has elected to bypass the consumer authentication flow via CardinalCommerce Rules Engine configuration.
      // Test card: 4000000000001083 (VISA)

      // Action : Show message: We could not verify your card, please try again or use a different card / payment method
      // This cannot happen as we want to have the liability shift so we wont activate this functionality
      captureException({
        message: 'lookup_bypassed',
      });
      braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      return buildRetryError();
    case 'challenge_required':
      // Failed Challenge Authentication
      // >>> Cardholder enrolled, authentication unsuccessful. Merchants should prompt customers for another form of payment.
      // Test card: 4000000000001109 (VISA)

      // Challenge Authentication is Unavailable
      // >>> Authentication unavailable for this transaction.
      // Test card: 4000000000001117 (VISA)

      // Action : Show message: We could not verify your card, please try again or use a different card / payment method
      captureException({
        message: 'challenge_required',
      });
      braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      return buildRetryError();
    default:
      captureException({
        message: `Unsupported BT 3DS authorization status code: ${threeDSecureInfo.status}`,
      });
      braintreeTracking('3DSsubmitKO', threeDSecureInfo.status);
      return buildNonRetryError();
  }

  const output: AuthorizePaymentResultSuccess = {
    success: true,
    nonce: paymentMethodPayload.nonce,
    liabilityShifted: threeDSecureInfo.liabilityShifted,
    liabilityShiftPossible: threeDSecureInfo.liabilityShiftPossible,
    status: threeDSecureInfo.status,
    deviceData: braintreeDataCollector.deviceData,
  };

  return output;
};
